import React, {useState} from "react";
import styled, {ThemeProvider} from "styled-components";
import {useAppSelector} from "../../app/hooks";
import {alertState} from "../../app/reducers/alert-slice";
import {scheduleState,} from "../../app/reducers/schedule-slice";
import theme from "../../style/theme";
import NoStartIcon from "../icons/NoStartIcon";
import TimeLineIcon from "../icons/TimeLineIcon/TimeLineIcon";
import WarningIcon from "../icons/WarningIcon";
import WithoutPhotoIcon from "../icons/WithoutPhotoIcon";
import {DelayContents} from "./DelayContents";
import {ExpectedDelayContents} from "./ExpectedDelayContents";
import {ScheduleNotStartedContents} from "./ScheduleNotStartedContents";
import {WithoutPhotoContents} from "./WithoutPhotoContents";
import {OperationAlertDeliveryGroup} from "../../types/alert/operation-alert-delivery-group";
import {OperationAlert} from "../../types/alert/operation-alert";
import {Schedule} from "../../types/schedule/schedule";

interface Props {
    children?: string;
    style?: React.CSSProperties;
    selectedStatus: string[];
    selectedClientIds: string[];
}

export const AlertPanel: React.FC<Props> = (
    {
        children,
        style,
        selectedStatus,
        selectedClientIds,
        ...rest
    }
) => {
    const {oldAlerts, allAlerts} = useAppSelector(alertState);

    const [fabSelector, setFabSelector] = useState("");
    const {schedulePageAll} = useAppSelector(scheduleState);

    const handleClickFabButton = (e: React.MouseEvent<HTMLButtonElement>) => {
        const value = e.currentTarget.value;
        if (value === fabSelector) {
            setFabSelector("");
            return;
        }
        setFabSelector(value);
    };

    function filterDeliveryGroups(
        deliveryGroups: OperationAlertDeliveryGroup[],
    ): OperationAlertDeliveryGroup[] {
        return deliveryGroups.filter(displaySchedule => {
            let statusCondition = true
            if (selectedStatus.length > 0) {
                const foundSchedule: Schedule | undefined = schedulePageAll.contents.find((s) => {
                    return s.id === displaySchedule.schedule.id
                })
                if (foundSchedule) {
                    statusCondition = selectedStatus.includes(foundSchedule.status)
                } else {
                    statusCondition = false
                }
            }

            let clientIdCondition = true
            if (selectedClientIds.length > 0) {
                clientIdCondition = selectedClientIds.some(clientId => displaySchedule.schedule.clientIds.includes(clientId));
            }

            return statusCondition && clientIdCondition;
        });
    }

    function filterNewAlerts(newAlerts: OperationAlertDeliveryGroup[], oldAlerts: OperationAlertDeliveryGroup[]): OperationAlertDeliveryGroup[] {
        return filterDeliveryGroups(
            newAlerts.filter(
                (alert) =>
                    oldAlerts.findIndex(
                        (a) =>
                            a.schedule.id === alert.schedule.id &&
                            a.deliveryGroupIndex === alert.deliveryGroupIndex
                    ) === -1
            )
        );
    }

    const filteredAllAlerts: OperationAlert = {
        schedulesNotStarted: filterDeliveryGroups(allAlerts.schedulesNotStarted),
        deliveryGroupsExpectedToBeDelayed: filterDeliveryGroups(allAlerts.deliveryGroupsExpectedToBeDelayed),
        deliveryGroupsCompletedWithDelay: filterDeliveryGroups(allAlerts.deliveryGroupsCompletedWithDelay),
        deliveryGroupsCompletedWithoutImage: filterDeliveryGroups(allAlerts.deliveryGroupsCompletedWithoutImage),
    };

    const newAlerts: OperationAlert = {
        schedulesNotStarted: filterNewAlerts(filteredAllAlerts.schedulesNotStarted, oldAlerts.schedulesNotStarted),
        deliveryGroupsExpectedToBeDelayed: filterNewAlerts(filteredAllAlerts.deliveryGroupsExpectedToBeDelayed, oldAlerts.deliveryGroupsExpectedToBeDelayed),
        deliveryGroupsCompletedWithDelay: filterNewAlerts(filteredAllAlerts.deliveryGroupsCompletedWithDelay, oldAlerts.deliveryGroupsCompletedWithDelay),
        deliveryGroupsCompletedWithoutImage: filterNewAlerts(filteredAllAlerts.deliveryGroupsCompletedWithoutImage, oldAlerts.deliveryGroupsCompletedWithoutImage),
    };

    return (
        <React.Fragment>
            <ThemeProvider theme={theme} {...rest}>
                <FabButtonContainer style={style}>
                    <FabButtonWrapper>
                        <FabButtonBox>
                            <FabButtonItem
                                onClick={handleClickFabButton}
                                value={"notStarted"}
                                className={`${fabSelector === "notStarted" && "active "}`}
                                borderRadius={"999px 0 0 999px"}
                            >
                                <NotStartedIconButton
                                    fill={
                                        newAlerts.schedulesNotStarted.length > 0
                                            ? theme.palette.primitives.red600
                                            : fabSelector === "notStarted"
                                                ? theme.palette.primitives.gray700
                                                : theme.palette.primitives.gray400
                                    }
                                />
                                <FabButtonLabel
                                    className={`${fabSelector === "notStarted" && "active "}`}
                                    applyRedColorTint={newAlerts.schedulesNotStarted.length > 0}
                                >
                                    미시작
                                </FabButtonLabel>
                                <DelayCounter
                                    className={`${fabSelector === "notStarted" && "active "}`}
                                    applyRedColorTint={newAlerts.schedulesNotStarted.length > 0}
                                >
                                    {filteredAllAlerts.schedulesNotStarted.length}건
                                </DelayCounter>
                                {fabSelector !== "notStarted" &&
                                    newAlerts.schedulesNotStarted.length > 0 && (
                                        <NewDelayCounter>
                                            {newAlerts.schedulesNotStarted.length}
                                        </NewDelayCounter>
                                    )}
                            </FabButtonItem>
                            <FabDivider/>
                            <FabButtonItem
                                onClick={handleClickFabButton}
                                value={"expectedDelay"}
                                className={`${fabSelector === "expectedDelay" && "active "}`}
                                borderRadius={"0"}
                            >
                                <ExpectedDelayIconButton
                                    fill={
                                        newAlerts.deliveryGroupsExpectedToBeDelayed.length > 0
                                            ? theme.palette.primitives.red600
                                            : fabSelector === "expectedDelay"
                                                ? theme.palette.primitives.gray700
                                                : theme.palette.primitives.gray400
                                    }
                                />
                                <FabButtonLabel
                                    className={`${fabSelector === "expectedDelay" && "active "}`}
                                    applyRedColorTint={newAlerts.deliveryGroupsExpectedToBeDelayed.length > 0}
                                >
                                    지연 예상
                                </FabButtonLabel>
                                <DelayCounter
                                    className={`${fabSelector === "expectedDelay" && "active "}`}
                                    applyRedColorTint={newAlerts.deliveryGroupsExpectedToBeDelayed.length > 0}
                                >
                                    {filteredAllAlerts.deliveryGroupsExpectedToBeDelayed.length}건
                                </DelayCounter>
                                {fabSelector !== "expectedDelay" &&
                                    newAlerts.deliveryGroupsExpectedToBeDelayed.length > 0 && (
                                        <NewDelayCounter>
                                            {newAlerts.deliveryGroupsExpectedToBeDelayed.length}
                                        </NewDelayCounter>
                                    )}
                            </FabButtonItem>
                            <FabDivider/>
                            <FabButtonItem
                                onClick={handleClickFabButton}
                                value={"delay"}
                                className={`${fabSelector === "delay" && "active "}`}
                                borderRadius={"0"}
                            >
                                <TimeIconButton
                                    fill={
                                        fabSelector === "delay"
                                            ? theme.palette.primitives.gray700
                                            : theme.palette.primitives.gray400
                                    }
                                />
                                <FabButtonLabel
                                    className={`${fabSelector === "delay" && "active "}`}
                                >
                                    지연 완료
                                </FabButtonLabel>
                                <DelayCounter
                                    className={`${fabSelector === "delay" && "active "}`}
                                >
                                    {filteredAllAlerts.deliveryGroupsCompletedWithDelay.length}건
                                </DelayCounter>
                                {fabSelector !== "delay" &&
                                    newAlerts.deliveryGroupsCompletedWithDelay.length > 0 && (
                                        <NewDelayCounter>
                                            {newAlerts.deliveryGroupsCompletedWithDelay.length}
                                        </NewDelayCounter>
                                    )}
                            </FabButtonItem>
                            <FabDivider/>
                            <FabButtonItem
                                onClick={handleClickFabButton}
                                value={"withoutPhoto"}
                                className={`${fabSelector === "withoutPhoto" && "active "}`}
                                borderRadius={"0 999px 999px 0"}
                            >
                                <WithoutPhotoButton
                                    fill={
                                        fabSelector === "withoutPhoto"
                                            ? theme.palette.primitives.gray700
                                            : theme.palette.primitives.gray400
                                    }
                                />
                                <FabButtonLabel
                                    className={`${fabSelector === "withoutPhoto" && "active "}`}
                                >
                                    촬영 누락
                                </FabButtonLabel>
                                <DelayCounter
                                    className={`${fabSelector === "withoutPhoto" && "active "}`}
                                >
                                    {filteredAllAlerts.deliveryGroupsCompletedWithoutImage.length}건
                                </DelayCounter>
                                {fabSelector !== "withoutPhoto" &&
                                    newAlerts.deliveryGroupsCompletedWithoutImage.length > 0 && (
                                        <NewDelayCounter>
                                            {newAlerts.deliveryGroupsCompletedWithoutImage.length}
                                        </NewDelayCounter>
                                    )}
                            </FabButtonItem>
                        </FabButtonBox>
                        {fabSelector === "notStarted" && (
                            <ScheduleNotStartedContents
                                newAlerts={newAlerts.schedulesNotStarted}
                                allAlerts={filteredAllAlerts.schedulesNotStarted}
                                oldAlerts={oldAlerts.schedulesNotStarted}
                            />
                        )}
                        {fabSelector === "expectedDelay" && (
                            <ExpectedDelayContents
                                newAlerts={newAlerts.deliveryGroupsExpectedToBeDelayed}
                                allAlerts={filteredAllAlerts.deliveryGroupsExpectedToBeDelayed}
                                oldAlerts={oldAlerts.deliveryGroupsExpectedToBeDelayed}
                            />
                        )}
                        {fabSelector === "delay" && (
                            <DelayContents
                                newAlerts={newAlerts.deliveryGroupsCompletedWithDelay}
                                allAlerts={filteredAllAlerts.deliveryGroupsCompletedWithDelay}
                                oldAlerts={oldAlerts.deliveryGroupsCompletedWithDelay}
                            />
                        )}
                        {fabSelector === "withoutPhoto" && (
                            <WithoutPhotoContents
                                newAlerts={newAlerts.deliveryGroupsCompletedWithoutImage}
                                allAlerts={filteredAllAlerts.deliveryGroupsCompletedWithoutImage}
                                oldAlerts={oldAlerts.deliveryGroupsCompletedWithoutImage}
                            />
                        )}
                    </FabButtonWrapper>
                </FabButtonContainer>
            </ThemeProvider>
        </React.Fragment>
    );
};

const FabButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  //left: -569.63px;
  left: -530px;
  //left: -427.63px;
`;

const FabButtonWrapper = styled.div`
  position: relative;
`;

const FabButtonBox = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid #bec3cb;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.16);
  border-radius: 999px;
  background: ${theme.palette.primitives.white};
  align-items: center;
`

interface FabButtonItemProps {
    borderRadius: string;
}

const FabButtonItem = styled.button<FabButtonItemProps>`
  position: relative;
  width: fit-content;
  height: 40px;
  background: ${theme.palette.primitives.white};
  color: ${theme.palette.primitives.gray700};
  padding: 0 12px 0 12px;
  transition: 180ms ease-in-out;
  border: 0;
  border-radius: ${(props) => props.borderRadius};
  cursor: pointer;
  display: flex;
  gap: 4px;
  align-items: center;

  &.active {
    background-color: ${theme.palette.primitives.yellow100};

    &:hover {
      background-color: ${theme.palette.primitives.yellow100};
    }
  }

  &:not(.active):hover {
    background-color: ${theme.palette.primitives.gray50};
  }
`;

const FabDivider = styled.div`
  width: 0.5px;
  height: 15px;
  background: #000000;
  opacity: 0.10;
`

const FabButtonLabel = styled.button<{
    applyRedColorTint?: boolean,
}>`
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  cursor: pointer;
  color: ${props => props.applyRedColorTint ? theme.palette.primitives.red600 : theme.palette.primitives.gray400};

  &.active {
    color: ${props => props.applyRedColorTint ? theme.palette.primitives.red600 : theme.palette.primitives.gray700};
  }
`;

const NotStartedIconButton = styled(NoStartIcon)`
  width: 16px;
  height: 16px;
  top: 10px;
  left: 11px;
  cursor: pointer;
`;

const ExpectedDelayIconButton = styled(WarningIcon)`
  width: 16px;
  height: 16px;
  top: 10px;
  left: 11px;
  cursor: pointer;
`;

const TimeIconButton = styled(TimeLineIcon)`
  width: 16px;
  height: 16px;
  top: 10px;
  left: 11px;
  cursor: pointer;
`;

const WithoutPhotoButton = styled(WithoutPhotoIcon)`
  width: 16px;
  height: 16px;
  top: 10px;
  left: 11px;
  cursor: pointer;
`;

const DelayCounter = styled.button<{
    applyRedColorTint?: boolean,
}>`
  font-weight: 900;
  font-size: 14px;
  border-radius: 999px;
  color: ${props => props.applyRedColorTint ? theme.palette.primitives.red600 : theme.palette.primitives.gray300};
  background-color: transparent;
  border: none;
  cursor: pointer;
  box-sizing: border-box;
  padding: 0;

  &.active {
    color: ${props => props.applyRedColorTint ? theme.palette.primitives.red600 : theme.palette.primitives.yellow600};
      //color: ${theme.palette.primitives.yellow600};
  }
`;

const NewDelayCounter = styled.button`
  position: absolute;
  min-width: 22px;
  height: 22px;
  padding: 3px;
  color: ${theme.palette.primitives.white};
  font-weight: 900;
  font-size: 12px;
  border-radius: 999px;
  background-color: ${theme.palette.primitives.red500};
  top: -8px;
  right: -4px;
  border: none;
  cursor: pointer;
  z-index: 9999;
`;
